import React, { useMemo, useState } from "react";
import { Card, Select, Table, Button } from "antd";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { getUserInfo } from "../../utils/redux/reducers/User";
import { ApiManager, Locale } from "../../utils";
import { defaultDateRange, buildCommissionListUrl, formatMoneyFen } from "../../utils/statistics/statisticsUtils";
import useStatisticsQuery from "./hooks/useStatisticsQuery";
import StatisticsDateFilter from "./components/StatisticsDateFilter";
import StatCardGrid, { buildMoneyCard } from "./components/StatCardGrid";
import TrendChart from "./components/TrendChart";
import PeriodCompare from "./components/PeriodCompare";
import ExportCsvButton from "./components/ExportCsvButton";
import "./statisticsShared.scss";

const api = ApiManager.getInstance();
const i18n = Locale.getInstance();

const STATUS_BUCKETS = ["all", "pending", "frozen", "processing", "paid"];
const ORDER_TYPES = ["all", "normal", "lottery", "server"];
const LEVELS = ["all", "1", "2", "3"];

function CommissionStatistics({ user }) {
  const [dateRange, setDateRange] = useState(defaultDateRange(7));
  const [statusBucket, setStatusBucket] = useState("all");
  const [orderType, setOrderType] = useState("all");
  const [level, setLevel] = useState("all");

  const filters = useMemo(() => {
    const params = {};
    if (statusBucket !== "all") params.status_bucket = statusBucket;
    if (orderType !== "all") params.order_type = orderType;
    if (level !== "all") params.level = level;
    return params;
  }, [statusBucket, orderType, level]);

  const { loading, data } = useStatisticsQuery({
    endpoint: api.statistics.commissions,
    token: user?.token,
    dateRange,
    filters,
  });

  const summary = data?.summary || {};
  const created = summary.created_in_range || {};
  const comparison = data?.comparison || {};
  const byStatus = data?.by_status || [];
  const topAgents = data?.top_agents || [];
  const commissionListUrl = useMemo(() => {
    if (!dateRange || dateRange.length !== 2) {
      return buildCommissionListUrl();
    }
    return buildCommissionListUrl(
      dateRange[0].format("YYYY-MM-DD"),
      dateRange[1].format("YYYY-MM-DD")
    );
  }, [dateRange]);

  const statCards = [
    { key: "pending", title: i18n.t("statisticsOverview.commissionPending"), value: summary.pending?.count ?? 0 },
    buildMoneyCard("pendingAmount", i18n.t("statisticsOverview.commissionPendingAmount"), summary.pending?.amount),
    { key: "processing", title: i18n.t("statisticsOverview.commissionProcessing"), value: summary.processing?.count ?? 0 },
    buildMoneyCard("paidAmount", i18n.t("statisticsOverview.commissionPaidAmount"), summary.paid?.amount),
    { key: "frozen", title: i18n.t("statisticsOverview.commissionFrozen"), value: summary.frozen?.count ?? 0 },
    { key: "createdCount", title: i18n.t("statisticsCommissions.createdCount"), value: created.count ?? 0 },
    buildMoneyCard("createdAmount", i18n.t("statisticsCommissions.createdAmount"), created.amount),
  ];

  const compareItems = [
    { key: "count", label: i18n.t("statisticsCommissions.createdCount"), data: comparison.count },
    { key: "amount", label: i18n.t("statisticsCommissions.createdAmount"), data: comparison.amount, isMoney: true },
  ];

  const statusColumns = [
    { title: i18n.t("statisticsCommissions.status"), dataIndex: "key", render: (v) => i18n.t(`statisticsCommissions.buckets.${v}`) },
    { title: i18n.t("statisticsCommissions.count"), dataIndex: "count" },
    { title: i18n.t("statisticsCommissions.amount"), dataIndex: "amount_fen", render: formatMoneyFen },
  ];

  const agentColumns = [
    { title: i18n.t("statisticsCommissions.agent"), dataIndex: "nickname", render: (v, r) => v || r.mobile || r.user_id },
    { title: i18n.t("statisticsCommissions.count"), dataIndex: "count" },
    { title: i18n.t("statisticsCommissions.amount"), dataIndex: "amount_fen", render: formatMoneyFen },
  ];

  return (
    <div className="statistics-page">
      <div className="statistics-page__header">
        <h2>{i18n.t("statisticsCommissions.title")}</h2>
        <Link to={commissionListUrl}>
          <Button type="link">{i18n.t("statisticsCommon.viewCommissions")}</Button>
        </Link>
      </div>

      <StatisticsDateFilter value={dateRange} onChange={setDateRange} loading={loading} />

      <Card className="statistics-page__section">
        <div style={{ marginBottom: 16 }}>
          <Select
            style={{ width: 140, marginRight: 8 }}
            value={statusBucket}
            onChange={setStatusBucket}
            options={STATUS_BUCKETS.map((v) => ({ value: v, label: i18n.t(`statisticsCommissions.buckets.${v}`) }))}
          />
          <Select
            style={{ width: 140, marginRight: 8 }}
            value={orderType}
            onChange={setOrderType}
            options={ORDER_TYPES.map((v) => ({ value: v, label: i18n.t(`statisticsCommissions.orderTypes.${v}`) }))}
          />
          <Select
            style={{ width: 120 }}
            value={level}
            onChange={setLevel}
            options={LEVELS.map((v) => ({ value: v, label: i18n.t(`statisticsCommissions.levels.${v}`) }))}
          />
        </div>
        <StatCardGrid items={statCards} loading={loading} columns={4} />
      </Card>

      <PeriodCompare items={compareItems} />

      <Card title={i18n.t("statisticsCommon.trend")} className="statistics-page__section">
        <TrendChart
          loading={loading}
          data={data?.trend}
          series={[
            { field: "count", name: i18n.t("statisticsCommissions.count"), color: "#1677ff" },
            { field: "amount_fen", name: i18n.t("statisticsCommissions.amount"), color: "#722ed1" },
          ]}
        />
      </Card>

      <Card
        title={i18n.t("statisticsCommissions.byStatus")}
        className="statistics-page__section"
        extra={<ExportCsvButton filename="commission-by-status.csv" columns={statusColumns} rows={byStatus} />}
      >
        <Table rowKey="key" loading={loading} columns={statusColumns} dataSource={byStatus} pagination={false} size="middle" />
      </Card>

      <Card
        title={i18n.t("statisticsCommissions.topAgents")}
        className="statistics-page__section"
        extra={<ExportCsvButton filename="top-agents.csv" columns={agentColumns} rows={topAgents} />}
      >
        <Table rowKey="user_id" loading={loading} columns={agentColumns} dataSource={topAgents} pagination={false} size="middle" />
      </Card>
    </div>
  );
}

export default connect((state) => ({ user: getUserInfo(state) }))(CommissionStatistics);
